// Signing with a finger.
//
// Pointer events rather than touch events, so the same code works for a finger
// on a phone, a stylus on a tablet and a mouse on a desktop — a manager
// completing a form at a desk should not find the signature box inert.
//
// `touch-action: none` is what makes it usable at all on a phone: without it
// the browser treats the first drag as a page scroll and the signature comes
// out as a single dot.
import { useEffect, useRef, useState } from 'react';

export function SignaturePad({
  onSigned,
  disabled,
  live = false,
  onCleared,
}: {
  /** Called with a PNG. The server accepts nothing else for a signature. */
  onSigned: (png: Blob) => void;
  disabled?: boolean;
  /**
   * Hand over the signature after every stroke rather than on a "Use this
   * signature" press (round 44, the timesheet): there the dialog's own
   * Submit is the one button, and a second confirm step in between read as
   * Submit being broken.
   */
  live?: boolean;
  /** Told when Clear wipes the pad, so a live caller drops what it held. */
  onCleared?: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  // The same fact as a ref: a stroke's end runs before the state above has
  // re-rendered, so the first stroke would otherwise read as no ink.
  const inked = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Backing store at device pixel ratio, or the line is a soft grey smear on
    // a phone screen — and a signature has to look like ink.
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * ratio);
    canvas.height = Math.round(rect.height * ratio);

    const context = canvas.getContext('2d');
    if (!context) return;
    context.scale(ratio, ratio);
    // White, not transparent: a transparent PNG signs itself invisibly onto a
    // white PDF page in some viewers and black-on-black in others.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, rect.width, rect.height);
    context.lineWidth = 2;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#111827';
  }, []);

  function positionOf(event: React.PointerEvent<HTMLCanvasElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  function start(event: React.PointerEvent<HTMLCanvasElement>) {
    if (disabled) return;
    // Capture, so a finger that slides off the box and back on keeps one
    // stroke rather than leaving a gap.
    event.currentTarget.setPointerCapture(event.pointerId);
    const context = canvasRef.current?.getContext('2d');
    if (!context) return;
    const { x, y } = positionOf(event);
    context.beginPath();
    context.moveTo(x, y);
    drawing.current = true;
  }

  function move(event: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current || disabled) return;
    const context = canvasRef.current?.getContext('2d');
    if (!context) return;
    const { x, y } = positionOf(event);
    context.lineTo(x, y);
    context.stroke();
    inked.current = true;
    setHasInk(true);
  }

  function end() {
    const wasDrawing = drawing.current;
    drawing.current = false;
    if (live && wasDrawing && inked.current) save();
  }

  function clear() {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    const rect = canvas.getBoundingClientRect();
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, rect.width, rect.height);
    inked.current = false;
    setHasInk(false);
    onCleared?.();
  }

  function save() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (blob) onSigned(blob);
    }, 'image/png');
  }

  return (
    <div>
      <canvas
        ref={canvasRef}
        // touch-action: none — see this file's header. Without it the first
        // drag scrolls the page and the signature is a dot.
        className="h-40 w-full touch-none rounded border border-gray-300 bg-white"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        onPointerLeave={end}
      />
      <div className="mt-2 flex gap-2">
        <button type="button" className="btn-ghost btn-sm" onClick={clear} disabled={disabled}>
          Clear
        </button>
        {!live && (
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={save}
            disabled={disabled || !hasInk}
          >
            Use this signature
          </button>
        )}
      </div>
    </div>
  );
}
